export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'void'
  | 'canceled'
  | 'written_off'

export type Customer = {
  id: number
  tenant_id: string
  display_name: string
  email: string | null
  phone: string | null
  billing_address_line1: string | null
  billing_address_line2: string | null
  billing_city: string | null
  billing_state: string | null
  billing_postal_code: string | null
  billing_country: string | null
  tax_id: string | null
  default_payment_terms_days: number
  default_currency: string
  notes: string | null
  stripe_customer_id: string | null
  is_active: boolean
  created_by_user_id: number | null
  created_at: string
  updated_at: string
}

export type CustomerPayload = Partial<
  Pick<
    Customer,
    | 'display_name' | 'email' | 'phone'
    | 'billing_address_line1' | 'billing_address_line2'
    | 'billing_city' | 'billing_state' | 'billing_postal_code' | 'billing_country'
    | 'tax_id' | 'default_payment_terms_days' | 'default_currency' | 'notes'
  >
> & {
  /** Optimistic concurrency: the updated_at the form read on load.
   *  Backend rejects with 409 STALE_VERSION if the row has changed
   *  since. Optional during rollout - omit to fall back to legacy
   *  unchecked write. */
  expected_updated_at?: string | null
}

export type InvoiceLine = {
  id?: number
  line_number: number
  description: string
  account_code: string
  quantity: number | string
  unit_price: number | string
  amount: number | string
  tax_rate: number | string
  tax_amount: number | string
}

export type InvoiceListItem = {
  id: number
  invoice_number: string
  po_number: string | null
  customer_id: number
  customer_display_name: string
  customer_email: string | null
  issue_date: string
  due_date: string
  currency: string
  subtotal: string
  tax_total: string
  discount: string
  total: string
  amount_paid: string
  status: InvoiceStatus
  sent_at: string | null
  paid_at: string | null
  voided_at: string | null
  posted_at: string | null
}

export type InvoiceStatusHistoryRow = {
  id: number
  from_status: InvoiceStatus | null
  to_status: InvoiceStatus
  reason: string | null
  actor_email: string | null
  created_at: string
}

export type InvoicePaymentRow = {
  id: number
  amount: string
  amount_applied: string
  currency: string
  received_at: string
  method: string
  provider: string | null
  status: string
}

export type InvoiceDetail = InvoiceListItem & {
  notes: string | null
  terms: string | null
  bank_details: string | null
  sender_logo_url: string | null
  sender_business_name: string | null
  sender_business_phone: string | null
  sender_business_email: string | null
  sender_address_line1: string | null
  sender_address_line2: string | null
  sender_city: string | null
  sender_country: string | null
  show_logo: boolean
  show_business_name: boolean
  show_business_phone: boolean
  show_business_email: boolean
  show_business_address: boolean
  show_bank_details: boolean
  /** Per-invoice snapshot of the payment collection method.
   *  null  = bank-transfer only, hosted /pay page hides the Pay button.
   *  Migration 090. */
  payment_collection_method: 'connect_express' | 'bokapsys_managed' | null
  pdf_s3_key: string | null
  first_viewed_at: string | null
  stripe_checkout_session_id: string | null
  stripe_payment_intent_id: string | null
  billing_address_line1: string | null
  billing_address_line2: string | null
  billing_city: string | null
  billing_state: string | null
  billing_postal_code: string | null
  billing_country: string | null
  // Migration 080: shipping + header-tax additions.
  shipping_amount: string | number | null
  shipping_account_code: string | null
  header_tax_rate_pct: string | number | null
  lines: InvoiceLine[]
  status_history: InvoiceStatusHistoryRow[]
  payments: InvoicePaymentRow[]
}

export type InvoicePayload = {
  customer_id: number
  issue_date?: string
  due_date?: string
  currency?: string
  notes?: string | null
  terms?: string | null
  bank_details?: string | null
  discount?: number
  /** Migration 080: optional flat shipping charge added to the total. */
  shipping_amount?: number
  /** Income/revenue COA code shipping income posts to. Required when
   *  shipping_amount > 0 (server validates). */
  shipping_account_code?: string | null
  /** When set, replaces per-line tax with a single rate applied to subtotal.
   *  Send `null` to clear (revert to per-line tax). Server enforces ≥ 0. */
  header_tax_rate_pct?: number | null
  po_number?: string | null
  sender_logo_url?: string | null
  sender_business_name?: string | null
  sender_business_phone?: string | null
  sender_business_email?: string | null
  sender_address_line1?: string | null
  sender_address_line2?: string | null
  sender_city?: string | null
  sender_country?: string | null
  show_logo?: boolean
  show_business_name?: boolean
  show_business_phone?: boolean
  show_business_email?: boolean
  show_business_address?: boolean
  show_bank_details?: boolean
  /** Per-invoice override of the tenant's payment collection method.
   *  Omit to inherit `tenants.payment_collection_mode`. Pass `null`
   *  explicitly to make this invoice bank-transfer only (the hosted
   *  /pay page hides the Pay button). Migration 090. */
  payment_collection_method?: 'connect_express' | 'bokapsys_managed' | null
  lines: Array<{
    description: string
    account_code: string
    quantity: number
    unit_price: number
    tax_rate?: number
  }>
}

export type InvoiceFilters = {
  status?: InvoiceStatus
  customer_id?: number
  date_from?: string
  date_to?: string
}

export type InvoicingSettings = {
  default_tax_rate_pct: number
  remittance_instructions: string
  number_prefix: string
  number_padding: number
  next_number: number
  reminder_enabled: boolean
  reminder_cadence_days: number[]
  reminder_max_count: number
}

export type InvoicingSettingsPatch = Partial<InvoicingSettings>

export type ArRecentActivity = {
  id: number
  created_at: string
  to_status: InvoiceStatus
  from_status: InvoiceStatus | null
  invoice_id: number
  invoice_number: string
  total: string
  currency: string
  customer_display_name: string
}

export type ArSummary = {
  outstanding_ar: number
  paid_this_month: number
  sent_count: number
  overdue_count: number
  draft_count: number
  paid_count: number
  recent_activity: ArRecentActivity[]
}

export type CreditNote = {
  id: number
  tenant_id: string
  customer_id: number
  source_invoice_id: number | null
  source_invoice_number: string | null
  credit_note_number: string
  amount: string
  currency: string
  issue_date: string
  reason: string | null
  status: 'issued' | 'applied' | 'void'
  amount_applied: string
  customer_display_name: string
  created_at: string
}

export type CreditNotePayload = {
  invoice_id: number
  amount: number
  issue_date?: string
  reason?: string
}

export type RefundPayload = {
  invoice_id: number
  customer_payment_id: number
  amount: number
  issued_at?: string
  method?: string
  reason?: string
  postToAccounts?: boolean
}

export type WriteOffPayload = {
  invoice_id: number
  amount?: number
  reason?: string
  bad_debt_account_code?: string
}

export type ArAgingBucket = {
  key: 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd91_plus'
  label: string
  min: number | null
  max: number | null
}

export type ArAgingInvoice = {
  id: number
  invoice_number: string
  issue_date: string
  due_date: string
  currency: string
  total: number
  amount_paid: number
  balance: number
  days_past_due: number
  bucket: ArAgingBucket['key']
  status: InvoiceStatus
  customer_id: number
  customer_display_name: string
  customer_email: string | null
}

export type ArAgingCustomerRow = {
  customer_id: number
  customer_display_name: string
  customer_email: string | null
  total: number
  current: number
  d1_30: number
  d31_60: number
  d61_90: number
  d91_plus: number
}

export type ArAgingReport = {
  as_of: string
  buckets: ArAgingBucket[]
  totals: {
    total: number
    current: number
    d1_30: number
    d31_60: number
    d61_90: number
    d91_plus: number
  }
  customers: ArAgingCustomerRow[]
  invoices: ArAgingInvoice[]
}

export type PortalInvoice = {
  id: number
  invoice_number: string
  issue_date: string
  due_date: string
  currency: string
  total: string | number
  amount_paid: string | number
  amount_due: number
  status: InvoiceStatus
  pay_token: string | null
}

export type PortalSnapshot = {
  customer: { id: number; display_name: string; email: string | null }
  business: {
    name: string | null
    email: string | null
    phone: string | null
    address_line1: string | null
    address_line2: string | null
    city: string | null
    state: string | null
    postal_code: string | null
    country: string | null
    logo_url: string | null
  }
  invoices: PortalInvoice[]
}

export type PortalTokenIssueResult = { token: string; url: string }

/**
 * Phase 3b - AR mutations that pass through the approval engine return
 * this marker when a policy matched. The caller should show a "pending
 * approval" toast and navigate the user to the approval instance detail
 * page instead of assuming the mutation ran.
 */
export type PendingApprovalResponse = {
  pending_approval: true
  approval_instance_id: number
  message: string
}

export function isPendingApproval(x: unknown): x is PendingApprovalResponse {
  return !!x && typeof x === 'object' && (x as { pending_approval?: unknown }).pending_approval === true
}

export type RecurringInvoiceCadence = 'weekly' | 'monthly' | 'quarterly' | 'yearly' | 'custom'

export type RecurringInvoiceTemplateSource = {
  id: number
  invoice_number: string
  customer_display_name: string | null
  total: string | number
  currency: string
}

export type RecurringInvoiceTemplate = {
  id: number
  tenant_id: string
  name: string
  /** Legacy single-source column. Kept in sync with source_invoice_ids[0]
   *  so old code continues to work; new code should use source_invoice_ids. */
  source_invoice_id: number
  /** Multi-source array - the runner clones every entry on each tick. */
  source_invoice_ids: number[]
  /** Hydrated by listTemplates so the editor + table view can show
   *  invoice numbers and customer names without an extra fetch. */
  source_invoices: RecurringInvoiceTemplateSource[]
  source_invoice_number: string | null
  customer_display_name: string | null
  cadence: RecurringInvoiceCadence
  cron_expression: string | null
  due_in_days: number
  is_active: boolean
  next_run_at: string | null
  last_run_at: string | null
  /** 'partial' surfaces when some sources cloned and others failed. */
  last_run_status: 'ok' | 'failed' | 'partial' | null
  last_run_error: string | null
  created_at: string
  updated_at: string
}

export type RecurringInvoiceTemplatePayload = {
  name: string
  /** New multi-source surface; clients should send this. */
  source_invoice_ids: number[]
  /** Back-compat - the backend still reads this when source_invoice_ids
   *  is missing, so older clients won't break. New clients need not set it. */
  source_invoice_id?: number
  cadence: RecurringInvoiceCadence
  cron_expression?: string
  due_in_days?: number
  is_active?: boolean
  start_at?: string
}

export type PublicInvoice = {
  invoice_number: string
  po_number: string | null
  issue_date: string
  due_date: string
  currency: string
  subtotal: string | number
  tax_total: string | number
  discount: string | number
  total: string | number
  amount_paid: string | number
  amount_due: number
  status: InvoiceStatus
  notes: string | null
  terms: string | null
  bank_details: string | null
  /** Per-invoice payment collection snapshot. Hosted /pay page renders
   *  the Pay button only when this is non-null. */
  payment_collection_method: 'connect_express' | 'bokapsys_managed' | null
  customer_display_name: string
  business_name: string | null
  business_email: string | null
  business_phone: string | null
  business_address_line1: string | null
  business_address_line2: string | null
  business_city: string | null
  business_state: string | null
  business_postal_code: string | null
  business_country: string | null
  logo_url: string | null
  lines: Array<{ description: string; amount: string | number }>
}
